import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import {
  Loader2, Search, FileUp, FileText, Image as ImageIcon, File, Download, Eye, Trash2,
  FolderOpen, HardDrive, Files,
} from 'lucide-react';
import api from '../lib/api';

const CATEGORIES = [
  { id: '', label: 'All categories' },
  { id: 'prescription', label: 'Prescriptions' },
  { id: 'product', label: 'Product images' },
  { id: 'patient_document', label: 'Patient documents' },
  { id: 'receipt', label: 'Receipts' },
  { id: 'asset_document', label: 'Asset documents' },
  { id: 'general', label: 'General' },
];

const CAT_LABELS = Object.fromEntries(CATEGORIES.filter(c => c.id).map(c => [c.id, c.label]));

const fmtSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const isImageType = (ct) => (ct || '').startsWith('image/');

const FileIcon = ({ ct, className = 'h-5 w-5' }) => {
  if (isImageType(ct)) return <ImageIcon className={className} />;
  if (ct === 'application/pdf') return <FileText className={className} />;
  return <File className={className} />;
};

export default function FileStoragePage() {
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [page, setPage] = useState(0);
  const limit = 30;

  // Upload
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('general');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadDesc, setUploadDesc] = useState('');

  // Preview
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ skip: String(page * limit), limit: String(limit) });
    if (catFilter) params.set('category', catFilter);
    if (search.trim()) params.set('q', search.trim());
    api.get(`/storage/files?${params}`)
      .then(r => { setFiles(r.data.files || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error('Failed to load files'))
      .finally(() => setLoading(false));
  }, [page, catFilter, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [catFilter, search]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('File too large. Max 2 MB.'); return; }
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('category', uploadCategory);
      if (uploadDesc.trim()) body.append('description', uploadDesc.trim());
      await api.post('/storage/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('File uploaded');
      setUploadDialogOpen(false);
      setUploadDesc('');
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Delete "${f.original_filename}"?`)) return;
    try {
      await api.delete(`/storage/files/${f.file_id}`);
      toast.success('File deleted');
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Delete failed');
    }
  };

  const preview = async (f) => {
    setPreviewFile(f);
    setPreviewLoading(true);
    try {
      const res = await api.get(`/storage/files/${f.file_id}/download`, { responseType: 'blob' });
      setPreviewUrl(URL.createObjectURL(res.data));
    } catch {
      toast.error('Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewFile(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const downloadFile = async (f) => {
    try {
      const res = await api.get(`/storage/files/${f.file_id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = f.original_filename || 'file';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const totalPages = Math.ceil(total / limit);

  // Summary stats
  const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);
  const imageCount = files.filter(f => f.is_image).length;
  const docCount = files.length - imageCount;

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="file-storage-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="file-storage-title">File Storage</h1>
          <p className="text-sm text-muted-foreground">Manage uploaded files, prescriptions, receipts and documents</p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)} data-testid="file-storage-upload-btn">
          <FileUp className="mr-2 h-4 w-4" /> Upload File
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="file-storage-stat-total">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50"><Files className="h-5 w-5 text-blue-600" /></div>
            <div><p className="text-xs text-muted-foreground">Total files</p><p className="text-lg font-semibold">{total}</p></div>
          </CardContent>
        </Card>
        <Card data-testid="file-storage-stat-images">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-50"><ImageIcon className="h-5 w-5 text-green-600" /></div>
            <div><p className="text-xs text-muted-foreground">Images</p><p className="text-lg font-semibold">{imageCount}</p></div>
          </CardContent>
        </Card>
        <Card data-testid="file-storage-stat-docs">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50"><FileText className="h-5 w-5 text-amber-600" /></div>
            <div><p className="text-xs text-muted-foreground">Documents</p><p className="text-lg font-semibold">{docCount}</p></div>
          </CardContent>
        </Card>
        <Card data-testid="file-storage-stat-size">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-50"><HardDrive className="h-5 w-5 text-purple-600" /></div>
            <div><p className="text-xs text-muted-foreground">This page</p><p className="text-lg font-semibold">{fmtSize(totalSize)}</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search files…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9" data-testid="file-storage-search" />
        </div>
        <Select value={catFilter || '_all'} onValueChange={(v) => setCatFilter(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-[180px]" data-testid="file-storage-cat-filter">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map(c => (
              <SelectItem key={c.id || '_all'} value={c.id || '_all'}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* File list */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : files.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground" data-testid="file-storage-empty">
          <FolderOpen className="h-12 w-12 mb-3 opacity-40" />
          <p className="text-sm">No files found</p>
          <p className="text-xs mt-1">Upload files or adjust your filters</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="file-storage-table">
              <thead className="bg-slate-50 dark:bg-slate-900">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">File</th>
                  <th className="px-3 py-2 text-left font-medium hidden sm:table-cell">Category</th>
                  <th className="px-3 py-2 text-left font-medium hidden md:table-cell">Size</th>
                  <th className="px-3 py-2 text-left font-medium hidden md:table-cell">Uploaded</th>
                  <th className="px-3 py-2 text-left font-medium hidden lg:table-cell">By</th>
                  <th className="px-3 py-2 text-right font-medium w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {files.map((f, i) => (
                  <tr key={f.file_id} className="hover:bg-slate-50/50 dark:hover:bg-slate-900/50" data-testid={`file-storage-row-${i}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileIcon ct={f.content_type} className="h-4 w-4 shrink-0 text-slate-500" />
                        <span className="truncate max-w-[200px]" title={f.original_filename}>{f.original_filename}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 hidden sm:table-cell">
                      <Badge variant="secondary" className="text-xs">{CAT_LABELS[f.category] || f.category}</Badge>
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell text-muted-foreground">{fmtSize(f.size)}</td>
                    <td className="px-3 py-2 hidden md:table-cell text-muted-foreground text-xs">{fmtDate(f.created_at)}</td>
                    <td className="px-3 py-2 hidden lg:table-cell text-muted-foreground text-xs truncate max-w-[120px]">{f.uploaded_by_name || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        {f.is_image && (
                          <Button variant="ghost" size="sm" onClick={() => preview(f)} data-testid={`file-storage-preview-${i}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => downloadFile(f)} data-testid={`file-storage-download-${i}`}>
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteFile(f)} className="text-red-600 hover:text-red-700" data-testid={`file-storage-delete-${i}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t bg-slate-50/50" data-testid="file-storage-pagination">
              <span className="text-xs text-muted-foreground">Page {page + 1} of {totalPages} · {total} files</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent data-testid="file-storage-upload-dialog">
          <DialogHeader><DialogTitle>Upload File</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Category</label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger data-testid="file-storage-upload-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.filter(c => c.id).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Description (optional)</label>
              <Input value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)}
                placeholder="Brief description" maxLength={300} data-testid="file-storage-upload-desc" />
            </div>
            <div>
              <input type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx"
                disabled={uploading} onChange={handleUpload} data-testid="file-storage-upload-input"
                className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-slate-100 file:text-slate-700 file:font-medium hover:file:bg-slate-200 file:cursor-pointer" />
              <p className="text-xs text-muted-foreground mt-1">Max 2 MB · Images, PDFs, CSV, TXT, Word, Excel</p>
            </div>
            {uploading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!previewFile} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-w-lg" data-testid="file-storage-preview-dialog">
          <DialogHeader><DialogTitle className="truncate">{previewFile?.original_filename}</DialogTitle></DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : previewUrl && previewFile?.is_image ? (
            <div className="rounded-lg overflow-hidden border bg-slate-50 flex items-center justify-center" style={{ maxHeight: '60vh' }}>
              <img src={previewUrl} alt={previewFile.original_filename} className="max-h-[60vh] w-auto object-contain" data-testid="file-storage-preview-image" />
            </div>
          ) : previewUrl && previewFile?.content_type === 'application/pdf' ? (
            <iframe src={previewUrl} title={previewFile.original_filename} className="w-full rounded-lg border" style={{ height: '60vh' }} data-testid="file-storage-preview-pdf" />
          ) : null}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => previewFile && downloadFile(previewFile)} data-testid="file-storage-preview-download-btn">
              <Download className="mr-2 h-4 w-4" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
